import React, { useState, useEffect, useRef } from 'react';
import { Container, Row, Col, Card, Button, Spinner, Alert, Modal } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { FaHeart, FaRegHeart, FaTrash, FaUpload, FaUser, FaSignInAlt } from 'react-icons/fa';
import { BiSolidCameraPlus, BiSolidCamera } from 'react-icons/bi';
import { MdPhotoCamera } from 'react-icons/md';
import { useUserAuth } from '../../context/UserAuthContext';
import api from '../../services/api';
import AccessibleLightbox from './AccessibleLightbox';
import './Photos.css';

// Photos fetched per page. Larger than the old button-driven value of 8 because
// infinite scroll would otherwise fire a request every two rows on desktop.
// Divisible by the 1 / 2 / 4 column counts so pages never end on a ragged row.
const PAGE_SIZE = 16;

// Start loading this far before the sentinel reaches the viewport, so the next
// page is usually already in the grid by the time the user scrolls to it.
const PREFETCH_MARGIN = '400px';

const PhotoGallery = () => {
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState('');
  const [hasMore, setHasMore] = useState(true);

  // The scroll observer reads the cursor through refs, never through state.
  // An IntersectionObserver callback keeps the closure it was created with, so
  // reading `lastId` from state there would re-request the page already loaded
  // and append duplicates.
  const lastIdRef = useRef(null);
  const isFetchingRef = useRef(false);
  const sentinelRef = useRef(null);

  // Set when a page fails. `hasMore` stays true on failure, so the sentinel
  // stays mounted and would otherwise re-fire on every scroll event against an
  // endpoint that is already failing. Cleared only by a manual retry.
  const autoLoadBlockedRef = useRef(false);

  // Always invoke the newest fetchPhotos from the observer, so the effect below
  // can be created once without capturing a stale version of it.
  const fetchPhotosRef = useRef(null);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [selectedPhotoIndex, setSelectedPhotoIndex] = useState(0);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [pendingLikePhotoId, setPendingLikePhotoId] = useState(null);
  const [pendingLikeState, setPendingLikeState] = useState(null);
  const { user } = useUserAuth();
  const { t } = useTranslation();

  useEffect(() => {
    fetchPhotos();

    // Add blue background to the body element directly
    document.body.style.backgroundColor = 'var(--tertiary-color)';

    // Cleanup function to reset body color when component unmounts
    return () => {
      document.body.style.backgroundColor = '';
    };
  }, []);

  const fetchPhotos = async (nextPage = false) => {
    // Synchronous guard: two intersections (or React's development double-effect)
    // can both pass a state-based check before either re-renders.
    if (isFetchingRef.current) return;

    // No cursor yet means there is no next page to ask for. Falling through
    // would re-request page one and append it a second time.
    if (nextPage && !lastIdRef.current) return;

    try {
      isFetchingRef.current = true;
      if (nextPage) {
        setLoadingMore(true);
      } else {
        setLoading(true);
      }
      setError('');

      let url = `/photos?limit=${PAGE_SIZE}`;
      if (nextPage) {
        url += `&lastId=${lastIdRef.current}`;
      }

      const response = await api.get(url);
      const fetchedPhotos = response.data.photos;
      const pagination = response.data.pagination;
      
      // Get locally stored liked photos
      const likedPhotos = getLikedPhotos();
      
      // Process photos with both server and local like state
      const processedPhotos = fetchedPhotos.map(photo => {
        // First check if the server says it's liked
        let isLiked = photo.isLiked === true;
        
        // If user is logged in, also check local storage state as a fallback
        if (user && !isLiked) {
          // If local storage says it's liked, trust that even if server disagrees
          isLiked = !!likedPhotos[photo.id];
        }
        
        return {
          ...photo,
          isLiked
        };
      });

      if (nextPage) {
        // Deduplicate by id. A photo uploaded between two page requests shifts
        // the cursor window, so the same document can arrive twice; React would
        // then warn about duplicate keys and render the photo twice.
        setPhotos(prev => {
          const seen = new Set(prev.map(photo => photo.id));
          return [...prev, ...processedPhotos.filter(photo => !seen.has(photo.id))];
        });
      } else {
        setPhotos(processedPhotos);
      }

      lastIdRef.current = pagination.lastId;

      // The server reports hasMore as `photos.length === limit`, so an exactly
      // full final page claims another page exists. Stop on the empty response
      // rather than leaving the sentinel to retry a page that has no cursor.
      setHasMore(pagination.hasMore && !!pagination.lastId && processedPhotos.length > 0);
      autoLoadBlockedRef.current = false;
    } catch (err) {
      console.error('Error fetching photos:', err);
      setError('Failed to load photos. Please try again later.');
      // Hand control back to the button instead of retrying on every scroll.
      autoLoadBlockedRef.current = true;
    } finally {
      isFetchingRef.current = false;
      setLoading(false);
      setLoadingMore(false);
    }
  };

  fetchPhotosRef.current = fetchPhotos;

  // Auto-load the next page as the sentinel below the grid comes into view.
  // Depends on photos.length as well as hasMore: the sentinel is not mounted
  // during the first load, and hasMore stays true across that transition, so
  // without it the effect would run once against a null ref and never attach.
  useEffect(() => {
    const sentinel = sentinelRef.current;

    // No sentinel means nothing left to load. Older browsers without the API
    // simply keep using the button.
    if (!sentinel || !hasMore || typeof IntersectionObserver === 'undefined') {
      return;
    }

    const observer = new IntersectionObserver(
      entries => {
        if (entries[0].isIntersecting && !autoLoadBlockedRef.current) {
          fetchPhotosRef.current(true);
        }
      },
      { rootMargin: PREFETCH_MARGIN }
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [hasMore, photos.length]);

  // Local storage helper to track likes between sessions
  const getLikedPhotos = () => {
    try {
      // Include user ID in the key to keep likes separate for each user
      const key = user ? `likedPhotos_${user.uid}` : 'likedPhotos_anonymous';
      const likesStr = localStorage.getItem(key);
      return likesStr ? JSON.parse(likesStr) : {};
    } catch (error) {
      console.error('Error getting liked photos from local storage:', error);
      return {};
    }
  };

  const setLikedPhoto = (photoId, isLiked) => {
    try {
      const likedPhotos = getLikedPhotos();
      
      if (isLiked) {
        likedPhotos[photoId] = true;
      } else {
        delete likedPhotos[photoId];
      }
      
      // Include user ID in the key to keep likes separate for each user
      const key = user ? `likedPhotos_${user.uid}` : 'likedPhotos_anonymous';
      localStorage.setItem(key, JSON.stringify(likedPhotos));
      
      console.log(`Updated local storage: Photo ${photoId} is now ${isLiked ? 'liked' : 'unliked'}`);
    } catch (error) {
      console.error('Error saving liked photos to local storage:', error);
    }
  };

  // Check if a photo is locally marked as liked
  const isPhotoLikedLocally = (photoId) => {
    const likedPhotos = getLikedPhotos();
    return !!likedPhotos[photoId];
  };

  // Update local storage on mount
  useEffect(() => {
    if (photos.length > 0 && user) {
      const likedPhotos = {};
      
      // Update local storage based on current photos isLiked state
      photos.forEach(photo => {
        if (photo.isLiked) {
          likedPhotos[photo.id] = true;
        }
      });
      
      localStorage.setItem('likedPhotos', JSON.stringify(likedPhotos));
    }
  }, [photos, user]);

  // Apply local likes to photos when they load
  // Effect to update photos with local like state when user or photos change
  useEffect(() => {
    if (photos.length > 0 && user) {
      const likedPhotos = getLikedPhotos();
      console.log('Applying local like state to photos');
      
      // Check if any photos need to be updated with local like state
      let needsUpdate = false;
      const updatedPhotos = photos.map(photo => {
        // If locally liked but not marked as liked in the data
        if (likedPhotos[photo.id] && !photo.isLiked) {
          needsUpdate = true;
          return { ...photo, isLiked: true };
        }
        return photo;
      });
      
      if (needsUpdate) {
        console.log('Updating photos with local like state');
        setPhotos(updatedPhotos);
      }
    }
  }, [photos, user]);
  
  // Effect to refresh photos when user changes (login/logout)
  useEffect(() => {
    // If user state changed and there's a user, fetch photos to get fresh data
    if (user) {
      console.log('User logged in, refreshing photos');
      fetchPhotos();
    }
  }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  // Function to handle redirects to login with return URL
  const redirectToLogin = (photoId = null, isLiked = null) => {
    // Store the current URL to return after login
    const currentPath = window.location.pathname;
    sessionStorage.setItem('returnUrl', currentPath);
    
    // If we have a photoId, this is coming from a like action
    if (photoId !== null) {
      setPendingLikePhotoId(photoId);
      setPendingLikeState(isLiked);
      setShowLoginModal(true);
    } else {
      // Direct redirect without confirmation
      window.location.href = '/login';
    }
  };
  
  // Handle confirmation to proceed to login
  const handleConfirmLogin = () => {
    setShowLoginModal(false);
    window.location.href = '/login';
  };
  
  // Handle cancellation of login
  const handleCancelLogin = () => {
    setShowLoginModal(false);
    setPendingLikePhotoId(null);
    setPendingLikeState(null);
  };

  const handleLike = async (photoId, isLiked) => {
    if (!user) {
      // Show login prompt if not authenticated
      redirectToLogin(photoId, isLiked);
      return;
    }
    
    console.log(`Attempting to ${isLiked ? 'unlike' : 'like'} photo ${photoId}`);
    
    // Check if the local storage state already matches what the user is trying to do
    const likedPhotos = getLikedPhotos();
    const isLikedInStorage = !!likedPhotos[photoId];
    
    console.log(`Photo currently liked in storage: ${isLikedInStorage}`);
    console.log(`Photo currently liked in UI: ${isLiked}`);
    
    // Force API call after login to ensure server state is updated correctly
    const forceApiCall = true;
    
    // If trying to like a photo that's already liked in storage, skip the API call
    // If trying to unlike a photo that's not liked in storage, skip the API call
    if (forceApiCall || (!isLiked && isLikedInStorage) || (isLiked && !isLikedInStorage)) {
      // Only proceed if there's a mismatch between displayed state and storage state
      // This prevents the 400 error caused by trying to like an already liked photo
      
      // Store the original photos state for rollback if needed
      const originalPhotos = [...photos];
  
      // Update local state optimistically first
      setPhotos(photos.map(photo => {
        if (photo.id === photoId) {
          return {
            ...photo,
            likeCount: isLiked ? Math.max((photo.likeCount || 0) - 1, 0) : (photo.likeCount || 0) + 1,
            isLiked: !isLiked
          };
        }
        return photo;
      }));
  
      // Update local storage to remember this like state
      setLikedPhoto(photoId, !isLiked);
  
      try {
        let response;
        
        // If the current state in the UI doesn't match the expected server state,
        // make the API call to sync them
        if (isLiked) {
          // Unlike
          response = await api.delete(`/photos/${photoId}/like`);
        } else {
          // Like
          response = await api.post(`/photos/${photoId}/like`, {});
        }
        
        // Update the like count from the server response to ensure consistency
        if (response?.data?.likeCount !== undefined) {
          setPhotos(currentPhotos => currentPhotos.map(photo => {
            if (photo.id === photoId) {
              return {
                ...photo,
                likeCount: response.data.likeCount,
                // Server confirms the like state
                isLiked: !isLiked
              };
            }
            return photo;
          }));
        }
        
      } catch (err) {
        if (err.response && err.response.status === 400) {
          // For 400 "already liked" errors, we'll just silently keep the state we optimistically updated to
          // No need to log error to console as this is an expected case
          
          // Get the current like state from the server's response
          const serverLikeState = err.response.data.message === 'User already liked this photo';
          
          // Quietly update local storage to match server state
          setLikedPhoto(photoId, serverLikeState);
          
          // Update UI silently - just make sure it matches what the server expects
          setPhotos(currentPhotos => currentPhotos.map(photo => {
            if (photo.id === photoId) {
              return {
                ...photo,
                isLiked: serverLikeState
              };
            }
            return photo;
          }));
          
          // Refresh photo data to get correct count
          const fetchPhotoData = async () => {
            try {
              const photoResponse = await api.get(`/photos/${photoId}`);
              if (photoResponse.data && photoResponse.data.photo) {
                setPhotos(currentPhotos => currentPhotos.map(photo => {
                  if (photo.id === photoId) {
                    return {
                      ...photo,
                      likeCount: photoResponse.data.photo.likeCount || 0,
                      isLiked: photoResponse.data.photo.isLiked || serverLikeState
                    };
                  }
                  return photo;
                }));
              }
            } catch (fetchErr) {
              // Silent error - just keep current state
              // We don't need to log this error as it's a graceful fallback
            }
          };
          
          // Fetch in background without blocking UI
          setTimeout(fetchPhotoData, 0);
        } else if (err.response) {
          // For other errors like 401, 403, 500, etc.
          setError(`Server error. Please try again.`);
          // Revert to original state
          setPhotos(originalPhotos);
          // Revert local storage state as well
          setLikedPhoto(photoId, isLiked);
        } else {
          // Network error or other client-side issues - log silently
          setError('Network error. Please check your connection and try again.');
          // Revert to original state
          setPhotos(originalPhotos);
          // Revert local storage state as well
          setLikedPhoto(photoId, isLiked);
        }
      }
    } else {
      // State is already what the user wants - let's just refresh the data to ensure counts are correct
      // This is for cases where displayed state and storage state already match
      try {
        const photoResponse = await api.get(`/photos/${photoId}`);
        if (photoResponse.data && photoResponse.data.photo) {
          setPhotos(currentPhotos => currentPhotos.map(photo => {
            if (photo.id === photoId) {
              return {
                ...photo,
                likeCount: photoResponse.data.photo.likeCount || 0
                // Keep the isLiked state as is since it's already correct
              };
            }
            return photo;
          }));
        }
      } catch (error) {
        // Silent error - just keep current state
      }
    }
  };

  const handleDelete = async (photoId) => {
    if (!user) return;

    if (window.confirm(t('confirmDelete'))) {
      try {
        await api.delete(`/photos/${photoId}`);

        // Remove from local state
        setPhotos(photos.filter(photo => photo.id !== photoId));
      } catch (err) {
        console.error('Error deleting photo:', err);
        setError('Failed to delete photo. Please try again.');
      }
    }
  };

  const navigateToUpload = () => {
    window.location.href = '/photos/upload';
  };
  
  // Open the lightbox when a photo is clicked
  const openLightbox = (index) => {
    // Check if the photo at this index exists
    if (photos[index]) {
      setSelectedPhotoIndex(index);
      setLightboxOpen(true);
      // Body scrolling will be handled by the lightbox component
      
      // Announce to screen readers that a lightbox has opened
      const announcement = document.createElement('div');
      announcement.setAttribute('aria-live', 'polite');
      announcement.setAttribute('role', 'status');
      announcement.setAttribute('class', 'visually-hidden');
      announcement.textContent = t('photoGallery.lightboxOpened');
      document.body.appendChild(announcement);
      
      // Remove the announcement after it's been read
      setTimeout(() => {
        if (document.body.contains(announcement)) {
          document.body.removeChild(announcement);
        }
      }, 1000);
    }
  };
  
  // Close the lightbox
  const closeLightbox = () => {
    setLightboxOpen(false);
    // Body scrolling will be handled by the lightbox component
    
    // Announce to screen readers that the lightbox has closed
    const announcement = document.createElement('div');
    announcement.setAttribute('aria-live', 'polite');
    announcement.setAttribute('role', 'status');
    announcement.setAttribute('class', 'visually-hidden');
    announcement.textContent = t('photoGallery.lightboxClosed');
    document.body.appendChild(announcement);
    
    // Remove the announcement after it's been read
    setTimeout(() => {
      if (document.body.contains(announcement)) {
        document.body.removeChild(announcement);
      }
    }, 1000);
  };

  if (loading && photos.length === 0) {
    return (
        <div className="photo-gallery-container">
          <Container className="text-center py-5">
            <Spinner animation="border" role="status" className="text-white">
              <span className="visually-hidden">{t('loading')}</span>
            </Spinner>
          </Container>
        </div>
    );
  }

  return (
      <div className="photo-gallery-container">
        <Container className="py-5">
          <div className="d-flex justify-content-between align-items-center mb-4">
            <h2>{t('photoGallery.title')}</h2>
            <Button
                variant="outline-light"
                onClick={navigateToUpload}
                className="upload-btn"
            >
              <FaUpload className="me-2" /> {t('photoGallery.uploadButton')}
            </Button>
          </div>

          {error && <Alert variant="danger">{error}</Alert>}

          {photos.length === 0 && !loading ? (
              <div className="text-center py-5">
                <p>{t('photoGallery.noPhotos')}</p>
                <Button variant="outline-light" onClick={navigateToUpload}>
                  {t('photoGallery.beFirstToUpload')}
                </Button>
              </div>
          ) : (
              <>
                <Row xs={1} md={2} lg={4} className="g-4">
                  {photos.map(photo => (
                      <Col key={photo.id}>
                        <Card className="photo-card h-100">
                          <div 
                            className="photo-img-container" 
                            onClick={() => openLightbox(photos.findIndex(p => p.id === photo.id))}
                            role="button"
                            tabIndex="0"
                            aria-label={t('photoGallery.viewFullPhoto', { 
                              description: photo.description || t('photoGallery.photoBy', { name: photo.userName }) 
                            })}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                openLightbox(photos.findIndex(p => p.id === photo.id));
                              }
                            }}
                          >
                            <img
                                src={photo.thumbnailUrl || photo.imageUrl}
                                alt={photo.description || t('photoGallery.photoBy', { name: photo.userName })}
                                className="card-img-top photo-img"
                                loading="lazy"
                                decoding="async"
                                ref={node => {
                                  // Covers images served from cache, whose load
                                  // event fires before React can attach onLoad.
                                  if (node && node.complete) {
                                    node.classList.add('is-loaded');
                                  }
                                }}
                                // Marking the element directly rather than via
                                // state avoids re-rendering the whole grid once
                                // per image, with hundreds of them on screen.
                                onLoad={e => e.currentTarget.classList.add('is-loaded')}
                                // Reveal broken images too, so a failed photo
                                // shows its alt text instead of shimmering forever.
                                onError={e => e.currentTarget.classList.add('is-loaded')}
                            />
                          </div>
                          <Card.Body>
                            <div className="d-flex justify-content-between align-items-start">
                              <div>
                                <Card.Title className="photo-uploader">
                                  <div className="photographer-credit">
                                    <MdPhotoCamera className="camera-icon" />
                                    <span className="photographer-name">© {photo.userName}</span>
                                  </div>
                                </Card.Title>
                                <Card.Text className="photo-description">
                                  {photo.description || t('photoGallery.noDescription')}
                                </Card.Text>
                              </div>
                              {user && (user.uid === photo.userId || user.role === 'admin') && (
                                  <Button
                                      variant="light"
                                      size="sm"
                                      className="delete-btn"
                                      onClick={() => handleDelete(photo.id)}
                                  >
                                    <FaTrash />
                                  </Button>
                              )}
                            </div>
                          </Card.Body>
                          <Card.Footer className="d-flex justify-content-between align-items-center">
                            <div className="d-flex align-items-center">
                              <Button
                                  variant="link"
                                  className="like-btn p-0 me-2"
                                  onClick={() => user ? handleLike(photo.id, photo.isLiked) : redirectToLogin(photo.id, photo.isLiked)}
                                  title={user ? (photo.isLiked ? t('unlike') : t('like')) : t('loginToLike')}
                              >
                                {photo.isLiked ? <FaHeart className="text-danger" /> : <FaRegHeart className="text-secondary" />}
                              </Button>
                              <span>{photo.likeCount || 0}</span>
                            </div>
                            <small className="text-muted">
                              <span title={new Date(photo.uploadedAt).toLocaleString()}>
                                {new Date(photo.uploadedAt).toLocaleDateString()} {new Date(photo.uploadedAt).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
                              </span>
                            </small>
                          </Card.Footer>
                        </Card>
                      </Col>
                  ))}
                </Row>

                {hasMore && (
                    <div ref={sentinelRef} className="text-center mt-4">
                      {loadingMore ? (
                          <Spinner
                              animation="border"
                              role="status"
                              className="text-white"
                          >
                            <span className="visually-hidden">
                              {t('loading', 'Loading...')}
                            </span>
                          </Spinner>
                      ) : (
                          // Kept as the keyboard/screen-reader path and the retry
                          // control after a failed page — scrolling alone is not a
                          // reachable action for every visitor.
                          <Button
                              variant="outline-light"
                              onClick={() => {
                                autoLoadBlockedRef.current = false;
                                fetchPhotos(true);
                              }}
                          >
                            {t('photoGallery.loadMore')}
                          </Button>
                      )}
                    </div>
                )}
              </>
          )}
          
          {/* Accessible Lightbox Component */}
          {lightboxOpen && photos.length > 0 && (
            <AccessibleLightbox
              photos={photos}
              startIndex={selectedPhotoIndex}
              isOpen={lightboxOpen}
              onClose={closeLightbox}
              onLike={handleLike}
              user={user}
            />
          )}
          
          {/* Login Confirmation Modal */}
          <Modal
            show={showLoginModal}
            onHide={handleCancelLogin}
            centered
            size="sm"
          >
            <Modal.Header closeButton>
              <Modal.Title>{t('loginRequired')}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <p>{t('loginToLikeMessage')}</p>
              <div className="text-center mt-3">
                <a 
                  href="/terms-policy" 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  className="terms-link"
                  onClick={() => sessionStorage.setItem('returnToAfterTerms', window.location.pathname)}
                >
                  {t('viewTermsPolicy')}
                </a>
              </div>
            </Modal.Body>
            <Modal.Footer>
              <Button variant="secondary" onClick={handleCancelLogin}>
                {t('cancel')}
              </Button>
              <Button variant="primary" onClick={handleConfirmLogin}>
                <FaSignInAlt className="me-2" /> {t('login')}
              </Button>
            </Modal.Footer>
          </Modal>
        </Container>
      </div>
  );
};

export default PhotoGallery;